import { Directive, ElementRef, HostListener, Renderer2, forwardRef, inject, input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { FormsValidators } from '@shared/components/forms/validators';
import { KeyCodeNames } from '@shared/constants';
import { FormatService, FractionalMode } from '@shared/services';

const noop = () => {
  // Do nothing
};

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line no-use-before-define
  useExisting: forwardRef(() => CurrencyMaskDirective),
  multi: true,
};

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[currencyMask]',
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
})
export class CurrencyMaskDirective implements ControlValueAccessor {
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly formatService = inject(FormatService);

  private readonly el: HTMLInputElement;
  private modelValue: any;

  public readonly allowFractional = input(false, { alias: 'currencyMaskAllowFractional' });

  public constructor() {
    const elementRef = this.elementRef;

    this.el = elementRef.nativeElement;
  }

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (a: any) => void = noop;

  public setDisabledState(disabled: boolean): void {
    this.el.disabled = disabled;
  }

  @HostListener('focus', ['$event'])
  @HostListener('blur', ['$event'])
  public onBlur(event: Event) {
    const target = event.target as HTMLInputElement | null;
    const value = target?.value ?? '';
    if (value !== '') {
      this.onTouchedCallback();

      const valid = FormsValidators.greaterThanZero(value);

      if (!valid) {
        this.el.value = '0';
        this.modelValue = 0;
      } else {
        // When the user defocuses the input, strip or add the pence as per the 'allowFractional' flag
        this.el.value = this.format(value, this.allowFractional() ? FractionalMode.Enforce : FractionalMode.Remove) ?? '';
        this.modelValue = this.parse(this.el.value);
        this.onChangeCallback(this.modelValue);
        if (this.modelValue) {
          this.renderer.setAttribute(this.elementRef.nativeElement, 'value', this.modelValue);
        }
      }
    }
  }

  @HostListener('change', ['$event'])
  public onChange(event: Event) {
    const target = event.target as HTMLInputElement | null;
    const value = target?.value ?? '';
    // When the user is typing, do not 'Enforce' decimals as this will disrupt the typing flow
    this.el.value = this.format(value, this.allowFractional() ? FractionalMode.Keep : FractionalMode.Remove) ?? '';
    this.modelValue = this.parse(this.el.value);
    this.onChangeCallback(this.modelValue);
    if (this.modelValue) {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'value', this.modelValue);
    }
  }

  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent) {
    if (((event.code as KeyCodeNames) ?? '') === KeyCodeNames.Tab) {
      return;
    }

    this.preventInvalidEntry(event);
  }

  @HostListener('keyup', ['$event'])
  public onKeyUp(event: KeyboardEvent) {
    const key = (event.code ?? '') as KeyCodeNames;
    if (key === KeyCodeNames.Tab) {
      return;
    }

    this.preventInvalidEntry(event);
    if (key === KeyCodeNames.Shift) {
      return;
    }
    let value: string | number = (event.target as HTMLInputElement).value.replace(/,/g, '');

    if (key === KeyCodeNames.ArrowUp) {
      value = +value + 1;
    } else if (key === KeyCodeNames.ArrowDown) {
      if (+value > 0) {
        value = +value - 1;
      }
    }

    // When the user is typing, do not 'Enforce' decimals as this will disrupt the typing flow
    this.el.value = this.format(value.toString(), this.allowFractional() ? FractionalMode.Keep : FractionalMode.Remove) ?? '';
    this.modelValue = this.parse(this.el.value);
    this.onChangeCallback(this.modelValue);
    if (this.modelValue) {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'value', this.modelValue);
    }
  }

  public get value(): unknown {
    return this.modelValue;
  }
  public set value(v: unknown) {
    if (v !== this.modelValue) {
      this.modelValue = v;
      this.onChangeCallback(v);
    }
  }

  public writeValue(value: any) {
    if (value !== this.modelValue) {
      // When writing value from programmatic changes, use the 'allowFractional'
      // to determine whether to strip or remove pence
      this.el.value = this.format(value, this.allowFractional() ? FractionalMode.Enforce : FractionalMode.Remove) ?? '';
      if (value) {
        this.renderer.setAttribute(this.elementRef.nativeElement, 'value', value);
      }
      this.modelValue = value;
    }
  }

  public registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  // to display value
  private format(value: string, mode: FractionalMode) {
    return this.formatService.formatCurrency(value, mode);
  }

  // to model
  private parse(value: string) {
    value = value.replace(/[^\d.]/g, '');
    return value;
  }

  private preventInvalidEntry(event: KeyboardEvent) {
    const key = (event.code ?? '') as KeyCodeNames;

    // Always allow these control keys
    const controlKeys = [
      KeyCodeNames.Delete,
      KeyCodeNames.Backspace,
      KeyCodeNames.Tab,
      KeyCodeNames.ArrowLeft,
      KeyCodeNames.ArrowRight,
      KeyCodeNames.ArrowUp,
      KeyCodeNames.ArrowDown,
      KeyCodeNames.Home,
      KeyCodeNames.End,
    ];

    if (controlKeys.includes(key)) {
      return;
    }

    // Allow digit keys
    const isDigit =
      (key >= KeyCodeNames.Digit0 && key <= KeyCodeNames.Digit9) || (key >= KeyCodeNames.Numpad0 && key <= KeyCodeNames.Numpad9);

    if (isDigit) {
      return;
    }

    // Allow decimal point only if fractional is enabled and no decimal exists
    const isDecimalKey = key === KeyCodeNames.Period || key === KeyCodeNames.NumpadPeriod;
    const currentValue = (event.target as HTMLInputElement).value;
    const allowDecimal = this.allowFractional() && !currentValue.includes('.') && isDecimalKey;

    if (allowDecimal) {
      return;
    }

    // Block all other keys
    event.preventDefault();
  }
}
